import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import {sortChatConversations, sortChatMessages} from '../../communicationUtils';
import ChatConversationList from './ChatConversationList';
import ChatMessageList from './ChatMessageList';

import backend from 'utils/backend';

const ChatList = (props) => {
  const {
    selectedParticipantKind,
    selectedParticipantRn,
    selectedParticipantResourceRn,
  } = props;
  const [chatConversations, setChatConversations] = React.useState([]);
  const [selectedChatConversationRn, setSelectedChatConversationRn] = React.useState(null);
  const [chatConversationsLoading, setChatConversationsLoading] = React.useState(false);
  const [chatConversationsLoadFailed, setChatConversationsLoadFailed] = React.useState(false);
  const [chatMessages, setChatMessages] = React.useState({});
  const [chatMessagesLoading, setChatMessagesLoading] = React.useState({});
  const [chatMessagesLoadFailed, setChatMessagesLoadFailed] = React.useState({});
  const chatConversationsRequestIdRef = React.useRef(0);
  const chatMessagesRequestIdRef = React.useRef({});

  const isCaregiver = selectedParticipantKind === 'CAREGIVER';
  const sortedChatConversations = React.useMemo(() => {
    return sortChatConversations(chatConversations);
  }, [chatConversations]);
  const selectedChatMessages = React.useMemo(() => {
    return sortChatMessages(chatMessages[selectedChatConversationRn] || []);
  }, [chatMessages, selectedChatConversationRn]);
  const selectedChatMessagesLoading = Boolean(chatMessagesLoading[selectedChatConversationRn]);
  const selectedChatMessagesLoadFailed = Boolean(chatMessagesLoadFailed[selectedChatConversationRn]);
  const showEmptySelectedChatMessages = Boolean(
      selectedChatConversationRn &&
      !selectedChatMessagesLoading &&
      !selectedChatMessagesLoadFailed &&
      selectedChatMessages.length === 0,
  );

  const loadChatConversations = React.useCallback(async () => {
    const requestId = chatConversationsRequestIdRef.current + 1;
    chatConversationsRequestIdRef.current = requestId;

    if (!isCaregiver || !selectedParticipantResourceRn) {
      setChatConversations([]);
      setSelectedChatConversationRn(null);
      setChatConversationsLoadFailed(false);
      return;
    }

    setChatConversationsLoading(true);
    setChatConversationsLoadFailed(false);

    try {
      const output = await backend.communication.getConversationList(selectedParticipantResourceRn);

      if (requestId !== chatConversationsRequestIdRef.current) return;

      setChatConversations(output?.list || []);
    } catch {
      if (requestId !== chatConversationsRequestIdRef.current) return;

      setChatConversations([]);
      setSelectedChatConversationRn(null);
      setChatConversationsLoadFailed(true);
    } finally {
      if (requestId === chatConversationsRequestIdRef.current) {
        setChatConversationsLoading(false);
      }
    }
  }, [isCaregiver, selectedParticipantResourceRn]);

  const loadChatMessages = React.useCallback(async (conversationRn) => {
    if (!conversationRn || !isCaregiver || !selectedParticipantResourceRn) {
      return;
    }

    const requestId = (chatMessagesRequestIdRef.current[conversationRn] || 0) + 1;
    chatMessagesRequestIdRef.current[conversationRn] = requestId;

    setChatMessagesLoading((current) => ({
      ...current,
      [conversationRn]: true,
    }));
    setChatMessagesLoadFailed((current) => ({
      ...current,
      [conversationRn]: false,
    }));

    try {
      let nextMessages = [];
      let lastKey = null;

      do {
        if (requestId !== chatMessagesRequestIdRef.current[conversationRn]) return;

        const output = await backend.communication.getConversationMessages(
            selectedParticipantResourceRn,
            conversationRn,
            lastKey,
        );

        nextMessages = [
          ...nextMessages,
          ...(output?.list || []),
        ];
        lastKey = output?.lastEvaluatedKey || null;
      } while (lastKey);

      if (requestId !== chatMessagesRequestIdRef.current[conversationRn]) return;

      setChatMessages((current) => ({
        ...current,
        [conversationRn]: nextMessages,
      }));
    } catch {
      if (requestId !== chatMessagesRequestIdRef.current[conversationRn]) return;

      setChatMessages((current) => ({
        ...current,
        [conversationRn]: [],
      }));
      setChatMessagesLoadFailed((current) => ({
        ...current,
        [conversationRn]: true,
      }));
    } finally {
      if (requestId === chatMessagesRequestIdRef.current[conversationRn]) {
        setChatMessagesLoading((current) => ({
          ...current,
          [conversationRn]: false,
        }));
      }
    }
  }, [isCaregiver, selectedParticipantResourceRn]);

  React.useEffect(() => {
    setChatConversations([]);
    setSelectedChatConversationRn(null);
    setChatConversationsLoadFailed(false);
    setChatMessages({});
    setChatMessagesLoading({});
    setChatMessagesLoadFailed({});
    chatMessagesRequestIdRef.current = {};
  }, [selectedParticipantResourceRn]);

  React.useEffect(() => {
    loadChatConversations();
  }, [loadChatConversations]);

  React.useEffect(() => {
    if (sortedChatConversations.length === 0) return;

    if (!selectedChatConversationRn || !sortedChatConversations.some((conversation) => conversation.conversationRn === selectedChatConversationRn)) {
      setSelectedChatConversationRn(sortedChatConversations[0].conversationRn);
    }
  }, [selectedChatConversationRn, sortedChatConversations]);

  React.useEffect(() => {
    if (!selectedChatConversationRn) return;
    if (chatMessages[selectedChatConversationRn] || chatMessagesLoading[selectedChatConversationRn]) return;

    loadChatMessages(selectedChatConversationRn);
  }, [chatMessages, chatMessagesLoading, loadChatMessages, selectedChatConversationRn]);

  if (!isCaregiver) {
    return (
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 400,
          color: '#6D7280',
        }}
      >
        <Translate expression='case.Communication.Chat.Unavailable' />
      </Typography>
    );
  }

  return (
    <Stack sx={{gap: 1.25}}>
      {
        chatConversationsLoading && sortedChatConversations.length === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )
      }
      {
        chatConversationsLoadFailed && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#D85D31',
            }}
          >
            <Translate expression='case.Communication.Chat.LoadFailed' />
          </Typography>
        )
      }
      {
        !chatConversationsLoading && !chatConversationsLoadFailed && sortedChatConversations.length === 0 && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#6D7280',
            }}
          >
            <Translate expression='case.Communication.Chat.Empty' />
          </Typography>
        )
      }
      {
        sortedChatConversations.length > 0 && (
          <ChatConversationList
            selectedChatConversationRn={selectedChatConversationRn}
            selectedParticipantRn={selectedParticipantRn}
            setSelectedChatConversationRn={setSelectedChatConversationRn}
            sortedChatConversations={sortedChatConversations}
          />
        )
      }
      {
        selectedChatConversationRn && selectedChatMessagesLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              py: 3,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )
      }
      {
        selectedChatConversationRn && selectedChatMessagesLoadFailed && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#D85D31',
            }}
          >
            <Translate expression='case.Communication.Chat.MessagesLoadFailed' />
          </Typography>
        )
      }
      {
        showEmptySelectedChatMessages && (
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 400,
              color: '#6D7280',
            }}
          >
            <Translate expression='case.Communication.Chat.NoMessages' />
          </Typography>
        )
      }
      {
        selectedChatMessages.length > 0 && (
          <ChatMessageList
            selectedChatMessages={selectedChatMessages}
            selectedParticipantRn={selectedParticipantRn}
          />
        )
      }
    </Stack>
  );
};

ChatList.propTypes = {
  selectedParticipantKind: PropTypes.string,
  selectedParticipantRn: PropTypes.string,
  selectedParticipantResourceRn: PropTypes.string,
};

export default React.memo(ChatList);
